import React, { useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Box, Button, VStack, useToast, useDisclosure } from "@chakra-ui/react";
import { EmailIcon } from "@chakra-ui/icons";
import BreakdownSummary from "components/Transaction/BreakdownSummary";
import PaymentSummary from "components/Transaction/PaymentSummary";
import MetaSummary from "components/Transaction/MetaSummary";
import TxnHeader from "components/Transaction/TxnHeader";
import PendingPayoutCancelButton from "components/Transaction/PendingPayoutCancelButton";
import { useSessionJWT, FLOW_STATE } from "utils";
import { sendTransactionReceiptEmail } from "api/invoicing";
import useGetTransactionById from "api/invoicing/useGetTransactionById";
import EmptyState from "components/EmptyState";
import Skeletons from "./Skeletons";
import EmailMeButton from "components/Transaction/EmailMeButton";
import { TXNDETAILS } from "flows/constants";

const SUPPORTED_VARIANTS = [
  "balance_transfer",
  "deposit",
  "user_purchase",
  "user_purchase_recipient",
  "payout",
  "user_purchase_escrow",
  "token_purchase",
  "token_convert",
  "refund",
];

/**
 * TxnDetailsContainer
 * @description
 * Shows the details of an invoice based on who is viewing it.
 * A seller and a buyer tied to the same invoice will see some different data, as only what is relevant is what is shown.
 * @param {object} props
 * @param {string} props.transactionId
 * @param {string} props.accountId - Used by Txn History to know which user's txn are we viewing.
 * @param {OnUpdate} [props.onUpdate]
 * @param {OnComplete} props.onComplete
 * @param {OnError} props.onError
 * @param {boolean} [props.hideExitButton = false] - By default we show the close button, but it can be hidden if needed.  Wrapping components can take the value from flowConfig.hideExitButtons
 * @returns JSX.Element
 */

export default function TxnDetailsContainer({
  transactionId,
  accountId,
  hideExitButton = false,
  onUpdate,
  onComplete,
  onError,
}) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const { sessionJWT } = useSessionJWT();
  const { data, isLoading, isError, error: loadErr, refetch } = useGetTransactionById(transactionId);
  const {
    isOpen: emailPopoverIsOpen,
    onToggle: onToggleEmailPopover,
    onClose: onCloseEmailPopover,
  } = useDisclosure();

  const [isLoadingSendEmail, setIsLoadingSendEmail] = useState(false);

  const handleCloseButtonClicked = () => {
    onComplete({
      state: FLOW_STATE.COMPLETE,
      trigger: "close-button",
    });
  };

  const handleSendEmailReceipt = async (email) => {
    setIsLoadingSendEmail(true);
    try {
      await sendTransactionReceiptEmail(sessionJWT, transactionId, email);
      onCloseEmailPopover();
      toast({
        title: formatMessage({
          defaultMessage: "Success",
          description: "Popup title",
        }),
        description: formatMessage({
          defaultMessage: "E-mail sent",
          description: "Popup description",
        }),
        status: "success",
        isClosable: true,
      });
      if (onUpdate) {
        onUpdate({
          flow: TXNDETAILS,
          type: "txndetails-receipt-email-sent",
        });
      }
    } catch (err) {
      onError && onError({ message: err.message });
      toast({
        title: formatMessage({
          defaultMessage: "Error sending e-mail",
          description: "Popup title",
        }),
        description:
          err.message ||
          formatMessage({
            defaultMessage: "Uknown error occured",
            description: "Error message",
          }),
        status: "error",
        isClosable: true,
      });
    }
    setIsLoadingSendEmail(false);
  };

  let content = null;

  if (isLoading) {
    content = <Skeletons />;
  } else if (isError) {
    content = <EmptyState error={loadErr} />;
    onError && onError({ message: loadErr.message });
  } else {
    const invoice = data.payload;
    const { transaction_type, transaction_status } = invoice;
    // check to make sure this is a txn details variant that we support
    if (SUPPORTED_VARIANTS.includes(transaction_type)) {
      const showEmailButton = !(
        transaction_type === "balance_transfer" ||
        (transaction_type === "payout" && transaction_status !== "processed") ||
        (transaction_type === "deposit" && transaction_status !== "processed") ||
        transaction_status === "failed"
      );
      content = (
        <div data-testid="txn-details-content">
          <Box
            p={4}
            pb={0}
            backgroundColor="lightBackground"
            borderY="1px solid"
            borderColor="lightBorder"
          >
            <VStack pt={10} pb={4}>
              <TxnHeader transaction={invoice} accountId={accountId} />
              {transaction_type === "payout" ? (
                <PendingPayoutCancelButton
                  transaction={invoice}
                  onRefetch={refetch}
                />
              ) : null}
              <Box pt={4} w="100%">
                <BreakdownSummary transaction={invoice} />
              </Box>
            </VStack>
          </Box>
          <Box p={4} pl={8}>
            <PaymentSummary transaction={invoice} accountId={accountId} />
          </Box>
          <Box p={4} pl={8}>
            <MetaSummary transaction={invoice} />
          </Box>
          <Box p={4}>
            {showEmailButton && (
              <EmailMeButton
                buttonText={formatMessage({
                  defaultMessage: "E-mail me a receipt",
                  description: "Button text",
                })}
                popupMessage={formatMessage({
                  defaultMessage: "E-mail me a receipt",
                  description: "Popup message",
                })}
                leftButtonIcon={<EmailIcon />}
                width="full"
                onSend={handleSendEmailReceipt}
                isLoading={isLoadingSendEmail}
                isOpen={emailPopoverIsOpen}
                onToggle={onToggleEmailPopover}
                onClose={onCloseEmailPopover}
              />
            )}
            {!hideExitButton && (
              <Button mt={4} variant="outline" width="full" onClick={handleCloseButtonClicked}>
                <FormattedMessage defaultMessage="Close" description="Button text" />
              </Button>
            )}
          </Box>
        </div>
      );
    } else {
      const err = new Error(
        formatMessage({
          defaultMessage: "Unsupported transaction type.",
          description: "Error message",
        })
      );
      onError && onError({ message: err.message });
      content = <EmptyState error={err} />;
    }
  }

  return <>{content}</>;
}
